// MembershipPaywall.tsx — native in-app membership purchase (RevenueCat), opened from
// the unlock walls and the You screen via openPaywall(). Native + SHOW_IAP only; it
// never mentions outside prices or any outside way to buy.
import React from 'react';
import { Browser } from '@capacitor/browser';
import { PURCHASES_ERROR_CODE } from '@revenuecat/purchases-capacitor';
import { Btn, Display, Body } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import { C, F, SIZE, W, SP, R } from '@/new/theme';
import { buildPlans } from '@/services/paywallPlans';
import { useAuth } from '@/hooks/useAuth';
import { useMembershipRecheckOnResume } from '@/hooks/useMembershipRecheckOnResume';
import {
  getMembershipPackages,
  buyPackage,
  restore,
  syncEntitlement,
  ensureIapLogin,
  storeName,
  type MembershipPackages,
  type MembershipOption,
} from '@/services/purchases';
import {
  alreadyMember,
  confirmMembership,
  runPurchase,
  runRestore,
  type FlowDeps,
  type FlowOutcome,
} from '@/services/paywallFlow';

function isPaymentPending(e: unknown): boolean {
  const code = e && typeof e === 'object' ? (e as { code?: unknown }).code : undefined;
  return String(code) === PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR;
}

const TERMS_URL = 'https://www.renurture.org/terms-of-purchase';
const PRIVACY_URL = 'https://www.renurture.org/privacy';

type Phase = 'checking' | 'member' | 'choose' | 'busy' | 'pending';
type PendingKind = 'purchase' | 'restore' | 'approval';
type Plan = 'annual' | 'monthly';

const COMING_SOON = 'Membership will be available here very soon.';

const BENEFITS: Array<{ icon: IconName; title: string; sub: string }> = [
  { icon: 'book', title: 'The full Recovery programme', sub: 'Step-by-step lessons to understand what happened, heal and rebuild' },
  { icon: 'headphones', title: 'Guided meditations', sub: 'Settle your body and steady your mind' },
  { icon: 'tool', title: 'Your toolkit', sub: 'Practical exercises for the hardest moments' },
  { icon: 'leaf', title: 'Calm Your Body', sub: 'Whenever you need to settle' },
  { icon: 'sparkle', title: 'Guide me', sub: 'Personal AI guidance to find what fits' },
  { icon: 'play', title: 'Live webinars with the founder', sub: 'Dr Newman on popular themes, with VIP access for members' },
];

export function MembershipPaywall({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const [phase, setPhase] = React.useState<Phase>('checking');
  const [pendingKind, setPendingKind] = React.useState<PendingKind>('purchase');
  const [pkgs, setPkgs] = React.useState<MembershipPackages | null>(null);
  const [plan, setPlan] = React.useState<Plan>('annual');
  const [note, setNote] = React.useState<string | null>(null);
  const [restoredAcct, setRestoredAcct] = React.useState(false);
  const store = storeName();
  const recheckRef = React.useRef(auth.recheck);
  recheckRef.current = auth.recheck;
  const uidRef = React.useRef<string | null>(null);
  uidRef.current = auth.session?.user.userId ?? null;

  const phaseRef = React.useRef<Phase>(phase);
  phaseRef.current = phase;
  // Not while the paywall's own purchase/restore is in flight: that flow settles itself.
  useMembershipRecheckOnResume(phase !== 'busy', () => {
    if (phaseRef.current !== 'busy') setPhase('member');
  });

  const liveRef = React.useRef(true);
  const sleepers = React.useRef(new Set<{ t: ReturnType<typeof setTimeout>; done: () => void }>());
  React.useEffect(() => {
    liveRef.current = true;
    const pending = sleepers.current;
    return () => {
      liveRef.current = false;
      pending.forEach((w) => { clearTimeout(w.t); w.done(); });
      pending.clear();
    };
  }, []);

  const sleep = (ms: number) => new Promise<void>((resolve) => {
    const w = { t: setTimeout(() => { sleepers.current.delete(w); resolve(); }, ms), done: resolve };
    sleepers.current.add(w);
  });

  const baseDeps = (): FlowDeps => ({
    buy: async () => {
      await ensureIapLogin(uidRef.current);
      const opt: MembershipOption | null | undefined = plan === 'annual' ? pkgs?.annual : pkgs?.monthly;
      if (!opt) throw new Error('No option selected');
      return buyPackage(opt.pkg);
    },
    restore: async () => {
      await ensureIapLogin(uidRef.current);
      return restore();
    },
    sync: syncEntitlement,
    recheck: () => recheckRef.current(),
    sleep,
    isLive: () => liveRef.current,
  });

  const loadOffers = async () => {
    setPkgs(null);
    try {
      await ensureIapLogin(uidRef.current).catch(() => undefined);
      const p = await getMembershipPackages();
      if (!liveRef.current) return;
      setPkgs(p);
      // Keep the selection on a plan that actually loaded (a fallback tap may have picked one that didn't).
      if (!p.annual && p.monthly) setPlan('monthly');
      else if (p.annual && !p.monthly) setPlan('annual');
    } catch {
      /* pkgs stays null: the fallback display shows */
    }
  };

  React.useEffect(() => {
    (async () => {
      if (await alreadyMember({ recheck: () => recheckRef.current() })) {
        if (liveRef.current) setPhase('member');
        return;
      }
      await loadOffers();
      if (liveRef.current) setPhase('choose');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settle = (outcome: FlowOutcome, kind: PendingKind = 'purchase') => {
    if (!liveRef.current || outcome === 'aborted') return;
    if (outcome === 'member') setPhase('member');
    else if (outcome === 'pending') { setPendingKind(kind); setPhase('pending'); }
    else if (outcome === 'not-found') {
      setNote('We couldn’t find a membership for this account.');
      setPhase('choose');
    } else setPhase('choose');
  };

  const subscribe = async () => {
    if (!(plan === 'annual' ? pkgs?.annual : pkgs?.monthly)) return;
    setPhase('busy');
    setNote(null);
    try {
      settle(await runPurchase(baseDeps()));
    } catch (e) {
      if (!liveRef.current) return;
      if (isPaymentPending(e)) {
        setPendingKind('approval');
        setPhase('pending');
        return;
      }
      setNote('That didn’t go through. Please try again, or tap Restore purchases if you were charged.');
      setPhase('choose');
    }
  };

  const doRestore = async () => {
    setPhase('busy');
    setNote(null);
    setRestoredAcct(false);
    try {
      const deps = baseDeps();
      let outcome = await runRestore(deps);
      if (outcome === 'not-found') {
        // Membership is one entitlement whatever route it was bought by: ask Supabase too.
        const isMember = await deps.recheck().catch(() => false);
        if (isMember && liveRef.current) { setRestoredAcct(true); outcome = 'member'; }
      }
      settle(outcome, 'restore');
    } catch {
      if (!liveRef.current) return;
      setNote('We couldn’t restore purchases just now. Please try again.');
      setPhase('choose');
    }
  };

  const retryConfirm = async () => {
    setPhase('busy');
    setNote(null);
    settle(await confirmMembership(baseDeps()), pendingKind);
  };

  const retryOffers = async () => {
    setPhase('busy');
    await loadOffers();
    if (liveRef.current) setPhase('choose');
  };

  const open = (url: string) => { Browser.open({ url }).catch(() => undefined); };
  const view = buildPlans(pkgs);
  const selected = plan === 'annual' ? pkgs?.annual : pkgs?.monthly;
  const cards: Array<[Plan, string, typeof view.annual]> = [
    ['annual', 'Yearly', view.annual],
    ['monthly', 'Monthly', view.monthly],
  ];

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 125,
        background: 'var(--cream)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: 'calc(var(--safe-top) + 56px) 30px calc(var(--safe-bottom) + 26px)',
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'calc(var(--safe-top) + 12px)',
          right: 16,
          background: 'none',
          border: 'none',
          color: 'var(--ink-soft)',
          minHeight: 44,
          minWidth: 44,
          cursor: 'pointer',
        }}
      >
        <Icon name="chevDown" size={22} />
      </button>

      {phase === 'checking' && <Body size={15}>Checking your membership…</Body>}

      {phase === 'member' && (
        <>
          {restoredAcct
            ? <Display parts={{ pre: 'Membership ', em: 'found', post: '.' }} size={26} lh={1.18} />
            : <Display parts={{ pre: 'You’re already a ', em: 'member', post: '.' }} size={26} lh={1.18} />}
          <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.6 }}>
            {restoredAcct ? 'You’re a member. Welcome back.' : 'Everything is unlocked. Thank you for being here.'}
          </Body>
          <div style={{ marginTop: 24, width: '100%', maxWidth: 320 }}>
            <Btn full size="lg" onClick={onClose}>Continue</Btn>
          </div>
        </>
      )}

      {phase === 'pending' && (
        <>
          {pendingKind === 'restore'
            ? <Display parts={{ pre: 'Membership ', em: 'found', post: '.' }} size={26} lh={1.18} />
            : pendingKind === 'approval'
            ? <Display parts={{ pre: 'Waiting for ', em: 'approval', post: '.' }} size={26} lh={1.18} />
            : <Display parts={{ pre: 'Purchase ', em: 'received', post: '.' }} size={26} lh={1.18} />}
          <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.6 }}>
            {pendingKind === 'restore'
              ? 'Membership found, unlocking… This can take a moment.'
              : pendingKind === 'approval'
              ? 'Your purchase is waiting for approval. You’ll be unlocked as soon as it goes through.'
              : 'Purchase received, unlocking… This can take a moment.'}
          </Body>
          <div style={{ marginTop: 24, width: '100%', maxWidth: 320 }}>
            <Btn full size="lg" onClick={retryConfirm}>Try again</Btn>
          </div>
        </>
      )}

      {(phase === 'choose' || phase === 'busy') && (
        <>
          <Display parts={{ pre: 'Become a ', em: 'member', post: '.' }} size={26} lh={1.18} />
          <Body size={15.5} style={{ margin: '12px auto 0', maxWidth: 330, lineHeight: 1.5 }}>
            Everything you need to recover, in one place.
          </Body>

          <ul style={{ listStyle: 'none', margin: `${SP.xxl}px 0 0`, padding: 0, width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: SP.lg, textAlign: 'left' }}>
            {BENEFITS.map((b) => (
              <li key={b.title} style={{ display: 'flex', gap: SP.md, alignItems: 'flex-start' }}>
                <span
                  aria-hidden
                  style={{
                    flex: '0 0 auto', width: 36, height: 36, borderRadius: R.sm, background: 'rgba(63,167,163,0.12)',
                    color: C.brandDark, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Icon name={b.icon} size={19} />
                </span>
                <span style={{ fontFamily: F.ui }}>
                  <span style={{ display: 'block', fontSize: SIZE.lg, fontWeight: W.bold, color: C.text, lineHeight: 1.3 }}>{b.title}</span>
                  <span style={{ display: 'block', fontSize: SIZE.md, color: C.textMuted, lineHeight: 1.4, marginTop: SP.xxs }}>{b.sub}</span>
                </span>
              </li>
            ))}
          </ul>
          <p style={{ margin: `${SP.lg}px auto 0`, maxWidth: 300, fontFamily: F.ui, fontSize: SIZE.body, fontWeight: W.medium, color: C.brandDark, lineHeight: 1.45 }}>
            Created by a Clinical Psychologist, based on what works in practice.
          </p>

          <div role="radiogroup" aria-label="Membership plan" style={{ marginTop: SP.xxl, width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cards.map(([key, label, v]) => {
              if (!v) return null;
              const on = plan === key;
              const isAnnual = key === 'annual';
              return (
                <button
                  key={key}
                  role="radio"
                  aria-checked={on}
                  onClick={() => setPlan(key)}
                  disabled={phase === 'busy'}
                  style={{
                    position: 'relative', textAlign: 'left', padding: '16px', borderRadius: R.lg, minHeight: 64, cursor: 'pointer',
                    background: on ? 'rgba(230,179,37,0.12)' : C.surface,
                    border: on ? `2px solid ${C.amberDark}` : `1px solid ${C.border}`,
                    color: C.text, fontFamily: F.ui,
                  }}
                >
                  {isAnnual && !view.fallback && (
                    <span
                      style={{
                        position: 'absolute', top: -10, right: 14, background: C.amberDark, color: '#fff', borderRadius: R.pill,
                        padding: '2px 10px', fontSize: SIZE.label, fontWeight: W.bold, letterSpacing: '0.4px',
                      }}
                    >
                      Best value
                    </span>
                  )}
                  <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: SP.md }}>
                    <span style={{ fontSize: SIZE.lg, fontWeight: W.bold }}>{label}</span>
                    <span style={{ fontSize: SIZE.lg, fontWeight: W.semibold }}>{v.priceString} {v.periodLabel}</span>
                  </span>
                  {isAnnual && v.perMonthString && (
                    <span style={{ display: 'block', marginTop: SP.xs, fontSize: SIZE.body, color: C.textMuted }}>
                      Just {v.perMonthString} a month{view.savingPercent ? ` · save ${view.savingPercent}%` : ''}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {view.fallback && (
            <div style={{ marginTop: SP.md, textAlign: 'center' }}>
              <Body role="status" size={13.5} style={{ margin: '0 auto', maxWidth: 300, lineHeight: 1.5 }}>{COMING_SOON}</Body>
              <button
                onClick={retryOffers}
                disabled={phase === 'busy'}
                style={{ ...linkStyle, marginTop: SP.xs }}
              >
                Try again
              </button>
            </div>
          )}

          {note && (
            <Body role="status" size={13.5} style={{ margin: '14px auto 0', maxWidth: 300, lineHeight: 1.5 }}>{note}</Body>
          )}

          <div style={{ marginTop: 20, width: '100%', maxWidth: 340 }}>
            <Btn full size="lg" onClick={subscribe} disabled={phase === 'busy' || !selected}>
              {phase === 'busy' ? 'Please wait…' : 'Subscribe'}
            </Btn>
            <button
              onClick={doRestore}
              disabled={phase === 'busy'}
              style={{
                width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--ink-soft)',
                fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, minHeight: 44, cursor: 'pointer',
              }}
            >
              Restore purchases
            </button>
          </div>

          <Body size={12.5} style={{ margin: '14px auto 0', maxWidth: 310, lineHeight: 1.5, opacity: 0.8 }}>
            Auto-renews until cancelled. Cancel any time in your {store} subscription settings.
          </Body>
          <div style={{ marginTop: 10, display: 'flex', gap: 18, justifyContent: 'center' }}>
            <button onClick={() => open(TERMS_URL)} style={linkStyle}>Terms of purchase</button>
            <button onClick={() => open(PRIVACY_URL)} style={linkStyle}>Privacy</button>
          </div>
        </>
      )}
    </div>
  );
}

const linkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--ink-soft)',
  textDecoration: 'underline',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 13,
  minHeight: 44,
  cursor: 'pointer',
};
